import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Plus, Trash2, ChevronDown, ChevronUp, Landmark, CreditCard,
  Home, Building2, ShoppingBag, Receipt, MoreHorizontal, Wallet, AlertCircle,
  CheckCircle2, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { ZoneHeader } from './ZoneHeader';

// Recognised liability types — MUST stay in sync with the backend
// LIABILITY_TYPES constant in /app/backend/routes/lender_submission_packs.py.
const LIABILITY_TYPES = [
  'ATO Debt',
  'Owner-Occupied Home Loan',
  'Investment Loan',
  'Business Loan',
  'Credit Card',
  'Personal Loan',
  'Buy Now, Pay Later',
  'Other Liability',
];

// Type-specific styling + icon + placeholder description hint.
const TYPE_META = {
  'ATO Debt':                 { icon: Receipt,         hint: 'e.g. ATO integrated client account — ABN 12 345 678', tone: '#dc2626', bg: '#fee2e2', ink: '#7f1d1d' },
  'Owner-Occupied Home Loan': { icon: Home,            hint: 'e.g. Westpac OO Home Loan — Ending XX1234',           tone: '#059669', bg: '#d1fae5', ink: '#064e3b' },
  'Investment Loan':          { icon: Building2,       hint: 'e.g. NAB Investment Loan — Ending XX5678',            tone: '#0d9488', bg: '#ccfbf1', ink: '#134e4a' },
  'Business Loan':            { icon: Landmark,        hint: 'e.g. Judo Business Loan — Ending XX9012',             tone: '#4f46e5', bg: '#e0e7ff', ink: '#312e81' },
  'Credit Card':              { icon: CreditCard,      hint: 'e.g. CBA Credit Card — Ending XX0002',                tone: '#7c3aed', bg: '#ede9fe', ink: '#4c1d95' },
  'Personal Loan':            { icon: Wallet,          hint: 'e.g. Latitude Personal Loan — Ending XX3456',         tone: '#d97706', bg: '#fef3c7', ink: '#78350f' },
  'Buy Now, Pay Later':       { icon: ShoppingBag,     hint: 'e.g. Afterpay — Account XX7788',                      tone: '#e11d48', bg: '#ffe4e6', ink: '#881337' },
  'Other Liability':          { icon: MoreHorizontal,  hint: 'e.g. HECS-HELP debt, family loan',                    tone: '#64748b', bg: '#f1f5f9', ink: '#0f172a' },
};

/**
 * LiabilitiesSection — the "Client Liabilities" step on the pack builder.
 * Broker captures every debt from the Fact Find + Credit Report so the
 * lender-submission checklist can personalise every relevant document
 * question against the actual account (e.g. "CBA Credit Card — Ending
 * XX0002 statement" rather than a generic "credit card statement").
 *
 * The list is persisted on the pack via a full-replace PATCH to
 * /api/lender-packs/{id} with `liabilities: [...]`. The backend also
 * mirrors the list to the client_profile collection so any future pack
 * for the same client auto-loads the same debts.
 */
export default function LiabilitiesSection({ pack, onPackChange, savePack, disabled }) {
  const liabilities = pack.liabilities || [];
  const [expanded, setExpanded] = useState(liabilities.length === 0);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ type: '', description: '', notes: '' });

  const commit = async (next) => {
    setBusy(true);
    try {
      const updated = await savePack({ liabilities: next });
      onPackChange?.(updated);
    } catch (e) {
      toast.error(`Failed to save liabilities — ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const addLiability = async () => {
    if (!draft.type || !draft.description.trim()) {
      toast.error('Pick a liability type and enter a recognisable description');
      return;
    }
    const next = [
      ...liabilities,
      {
        id: `lia-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: draft.type,
        description: draft.description.trim(),
        notes: (draft.notes || '').trim(),
      },
    ];
    await commit(next);
    setDraft({ type: '', description: '', notes: '' });
    toast.success('Liability added — checklist personalised');
  };

  const updateLiability = async (id, patch) => {
    const next = liabilities.map(l => l.id === id ? { ...l, ...patch } : l);
    await commit(next);
  };

  const removeLiability = async (id) => {
    if (!window.confirm('Remove this liability? Every checklist item personalised against it will be removed too.')) return;
    const next = liabilities.filter(l => l.id !== id);
    await commit(next);
    toast.success('Liability removed');
  };

  const togglePaidOut = async (id, paid) => {
    if (paid) {
      const ok = window.confirm(
        'Mark this liability as paid out?\n\n'
        + "Every checklist item personalised against this debt will be removed "
        + "from the pack. You can restore it later if it turns out to still be active."
      );
      if (!ok) return;
    }
    const next = liabilities.map(l => l.id === id ? { ...l, paid_out: paid } : l);
    await commit(next);
    toast.success(paid ? 'Liability marked as paid out — checklist updated' : 'Liability restored — checklist updated');
  };

  const liveCount = liabilities.filter(l => !l.paid_out).length;
  const paidCount = liabilities.length - liveCount;

  return (
    <div
      className="rounded-2xl border-2 shadow-md overflow-hidden bg-white"
      style={{ borderColor: '#a5b4fc', colorScheme: 'light' }}
      data-testid="pack-liabilities-section"
    >
      <ZoneHeader
        step={2}
        icon={Wallet}
        title="Client Liabilities"
        subtitle="Confirm every debt from the client's Fact Find + Credit Report (CCR). Each one added below personalises the checklist so credit and support teams see exactly which account each statement request relates to."
        accent="#4f46e5" headerBg="#eef2ff" border="#c7d2fe" ink="#312e81" mutedInk="#4338ca"
        testid="pack-liabilities-header"
        right={
          <>
            <div className="text-right hidden sm:block">
              <div className="text-[9px] font-black uppercase tracking-widest" style={{ color: '#4338ca' }}>
                {paidCount > 0 ? 'Live · Paid out' : 'Debts on file'}
              </div>
              <div className="text-lg font-black tabular-nums leading-none" style={{ color: liveCount ? '#4f46e5' : '#94a3b8' }}>
                {paidCount > 0
                  ? <><span style={{ color: '#4f46e5' }}>{liveCount}</span><span style={{ color: '#94a3b8' }}> · </span><span style={{ color: '#94a3b8' }}>{paidCount}</span></>
                  : liabilities.length}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="p-1.5 rounded-md hover:bg-white/60 transition-colors"
              data-testid="pack-liabilities-toggle"
            >
              {expanded ? <ChevronUp className="w-4 h-4" style={{ color: '#4338ca' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#4338ca' }} />}
            </button>
          </>
        }
      />

      {expanded && (
        <div className="p-4 space-y-3">
          {/* List of existing liabilities */}
          {liabilities.length === 0 ? (
            <div
              className="rounded-lg border-2 border-dashed p-4 flex items-start gap-3"
              style={{ borderColor: '#fcd34d', background: '#fffbeb' }}
              data-testid="liabilities-empty-state"
            >
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#d97706' }} />
              <div>
                <div className="text-[13px] font-black" style={{ color: '#78350f' }}>No liabilities added yet</div>
                <div className="text-[11.5px] mt-0.5" style={{ color: '#92400e' }}>
                  Confirm every debt on the client&rsquo;s Fact Find and Credit Report below.
                  Once added, the checklist auto-personalises every credit-card, home-loan,
                  BNPL and non-CCR statement request against the actual account.
                </div>
              </div>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="liabilities-list">
              {liabilities.map((l) => (
                <LiabilityRow
                  key={l.id}
                  liability={l}
                  onChange={(patch) => updateLiability(l.id, patch)}
                  onRemove={() => removeLiability(l.id)}
                  onTogglePaidOut={(v) => togglePaidOut(l.id, v)}
                  disabled={disabled || busy}
                />
              ))}
            </ul>
          )}

          {/* Add-new form */}
          <div
            className="rounded-lg p-3 border"
            style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}
            data-testid="liabilities-add-form"
          >
            <div className="text-[10px] font-black uppercase tracking-[0.14em] mb-2" style={{ color: '#64748b' }}>
              Add liability
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,220px)_1fr_auto] gap-2">
              <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                <SelectTrigger className="h-9 text-xs bg-white" data-testid="liability-type-trigger" style={{ color: '#0f172a' }}>
                  <SelectValue placeholder="Liability type" />
                </SelectTrigger>
                <SelectContent>
                  {LIABILITY_TYPES.map(t => {
                    const meta = TYPE_META[t];
                    const Icon = meta.icon;
                    return (
                      <SelectItem key={t} value={t} data-testid={`liability-type-option-${t}`}>
                        <span className="inline-flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5" style={{ color: meta.tone }} />
                          <span>{t}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder={draft.type ? (TYPE_META[draft.type]?.hint || 'Recognisable description') : 'Recognisable description — e.g. CBA Credit Card — Ending XX0002'}
                className="h-9 text-xs bg-white"
                data-testid="liability-description-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && draft.type && draft.description.trim() && !busy) {
                    e.preventDefault();
                    addLiability();
                  }
                }}
              />
              <Button
                onClick={addLiability}
                disabled={busy || !draft.type || !draft.description.trim()}
                className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-xs shrink-0"
                data-testid="liability-add-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            <Textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Internal notes (optional) — e.g. balance owing, interest rate, why it's being refinanced"
              rows={1}
              className="text-xs bg-white mt-2 resize-none min-h-[36px]"
              data-testid="liability-notes-input"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function LiabilityRow({ liability, onChange, onRemove, onTogglePaidOut, disabled }) {
  const meta = TYPE_META[liability.type] || TYPE_META['Other Liability'];
  const Icon = meta.icon;
  const paidOut = !!liability.paid_out;
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState({
    type: liability.type,
    description: liability.description,
    notes: liability.notes || '',
  });

  const save = () => {
    if (!local.type || !local.description.trim()) {
      toast.error('Type and description are required');
      return;
    }
    onChange({
      type: local.type,
      description: local.description.trim(),
      notes: local.notes.trim(),
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <li
        className="rounded-lg border p-3 space-y-2"
        style={{ borderColor: meta.tone, background: '#ffffff' }}
        data-testid={`liability-row-${liability.id}`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,220px)_1fr] gap-2">
          <Select value={local.type} onValueChange={(v) => setLocal({ ...local, type: v })}>
            <SelectTrigger className="h-8 text-xs bg-white" style={{ color: '#0f172a' }}><SelectValue /></SelectTrigger>
            <SelectContent>
              {LIABILITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            value={local.description}
            onChange={(e) => setLocal({ ...local, description: e.target.value })}
            className="h-8 text-xs bg-white"
            data-testid={`liability-edit-desc-${liability.id}`}
          />
        </div>
        <Textarea
          value={local.notes}
          onChange={(e) => setLocal({ ...local, notes: e.target.value })}
          rows={1}
          placeholder="Internal notes (optional)"
          className="text-xs bg-white resize-none min-h-[36px]"
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setLocal({ type: liability.type, description: liability.description, notes: liability.notes || '' }); }} className="h-7 text-[11px]" data-testid={`liability-edit-cancel-${liability.id}`}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={disabled} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`liability-edit-save-${liability.id}`}>
            Save
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li
      className="rounded-lg border relative overflow-hidden"
      style={{
        borderColor: paidOut ? '#cbd5e1' : '#e2e8f0',
        background: paidOut ? '#f8fafc' : '#ffffff',
        opacity: paidOut ? 0.78 : 1,
      }}
      data-testid={`liability-row-${liability.id}`}
      data-paid-out={paidOut ? 'true' : 'false'}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: paidOut ? '#94a3b8' : meta.tone }} />
      <div className="pl-3 pr-2 py-2.5 flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: paidOut ? '#e2e8f0' : meta.bg }}>
          <Icon className="w-4 h-4" style={{ color: paidOut ? '#64748b' : meta.tone }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: paidOut ? '#64748b' : meta.tone }}>{liability.type}</div>
            {paidOut && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ background: '#dcfce7', color: '#166534' }}
                data-testid={`liability-paid-badge-${liability.id}`}
              >
                <CheckCircle2 className="w-2.5 h-2.5" /> Paid out
              </span>
            )}
          </div>
          <div
            className="text-[13px] font-black leading-tight mt-0.5"
            style={{
              color: paidOut ? '#64748b' : meta.ink,
              textDecoration: paidOut ? 'line-through' : 'none',
            }}
          >
            {liability.description}
          </div>
          {liability.notes && (
            <div className="text-[11px] italic mt-1" style={{ color: '#64748b' }}>{liability.notes}</div>
          )}
          {paidOut && (
            <div className="text-[10.5px] font-semibold mt-1" style={{ color: '#166534' }}>
              Statement no longer requested from the checklist.
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {paidOut ? (
            <button
              type="button"
              onClick={() => onTogglePaidOut(false)}
              className="p-1.5 rounded hover:bg-slate-100"
              disabled={disabled}
              title="Restore — mark as still active"
              data-testid={`liability-restore-${liability.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5" style={{ color: '#0d9488' }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onTogglePaidOut(true)}
              className="p-1.5 rounded hover:bg-emerald-50"
              disabled={disabled}
              title="Mark as paid out — removes personalised checklist items for this debt"
              data-testid={`liability-paid-${liability.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#059669' }} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="p-1.5 rounded hover:bg-slate-100"
            disabled={disabled}
            title="Edit"
            data-testid={`liability-edit-${liability.id}`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 rounded hover:bg-rose-50"
            disabled={disabled}
            title="Remove"
            data-testid={`liability-remove-${liability.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" style={{ color: '#e11d48' }} />
          </button>
        </div>
      </div>
    </li>
  );
}
